'use client';

import React, { useEffect, useState } from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { useResponsive } from '@depop/web-ui-kit/useResponsive';
import { useTranslations } from 'next-intl';
import { type Entries } from 'type-fest';

import { YouMightAlsoLikeModal } from './YouMightAlsoLikeModal';
import styles from './styles.module.css';

import { YouMightAlsoLikePlaceholder } from '@/app/[locale]/products/[slug]/components/Modules/YouMightAlsoLike/YouMightAlsoLikePlaceholder';
import { ButtonMinimal } from '@/components/ButtonMinimal';
import { SimilarItems } from '@/components/SimilarItems';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { ImpressionsBatcherProvider } from '@/modules/impressions/ImpressionsBatcherProvider';
import { ModuleName } from '@/modules/impressions/types';
import { useProductBySlugBasicPDP } from '@/modules/product/hooks/useProductBySlugBasicPDP';
import { useProductBySlugExtendedPDP } from '@/modules/product/hooks/useProductBySlugExtendedPDP';
import { type SimilarProduct } from '@/modules/similar/aliases';
import {
  MIN_PRODUCTS_PER_SEGMENT,
  YMAL_DESKTOP_PRODUCTS_PER_SEGMENT_VISIBLE_ON_PAGE,
  YMAL_PRODUCTS_TO_SEGMENT_COUNT,
} from '@/modules/similar/constants';
import { getSegmentTitleArgs } from '@/modules/similar/helpers/getSegmentTitleArgs';
import { useSegments } from '@/modules/similar/hooks/useSegments';
import { useYouMightAlsoLike } from '@/modules/similar/hooks/useYouMightAlsoLike';
import { type YMALSegmentKeys } from '@/modules/similar/types';

type Props = {
  renderAfterFirstSegment?: React.ReactNode;
};

export function YouMightAlsoLike({ renderAfterFirstSegment }: Props = {}) {
  const tCommon = useTranslations('common');
  const { sendActivityTrackerEvent } = useActivityTracker();
  const { data: product } = useProductBySlugBasicPDP();
  const { data: productExtended } = useProductBySlugExtendedPDP();
  const { isMaxMedium: isMobile } = useResponsive();

  const { products, isLoading } = useYouMightAlsoLike(
    YMAL_PRODUCTS_TO_SEGMENT_COUNT
  );

  const { segments, itemsPerRow } = useSegments(products);

  const [selectedSegment, setSelectedSegment] =
    useState<YMALSegmentKeys | null>(null);

  function getIsSeeMoreButtonDisplayed(products: SimilarProduct[]) {
    return (
      !isMobile &&
      products.length > YMAL_DESKTOP_PRODUCTS_PER_SEGMENT_VISIBLE_ON_PAGE
    );
  }

  function handleSeeMoreClick(segmentName: YMALSegmentKeys) {
    setSelectedSegment(segmentName);
  }

  function handleModalOpen(value: boolean) {
    if (!value) {
      setSelectedSegment(null);
    }
  }

  function handleProductClick(
    product: SimilarProduct,
    itemPosition: number,
    segmentName: string
  ) {
    sendActivityTrackerEvent(ActivityTrackerEventType.MODULE_CLICK_ACTION, {
      moduleName: `pdp_ymal_segment_${segmentName}`,
      itemClicked: product.id.toString(),
      itemPosition,
      schemaVersion: '3.0',
    });
  }

  useEffect(() => {
    if (!isLoading && product && !products.length) {
      sendActivityTrackerEvent(ActivityTrackerEventType.NO_SIMILAR_ITEMS, {
        productId: product.id,
        schemaVersion: '3.0',
      });
    }
  }, [isLoading]);

  if (isLoading) {
    return <YouMightAlsoLikePlaceholder />;
  }

  if (!product || !products.length) {
    return null;
  }

  function getSegmentTitle(selectedSegment: YMALSegmentKeys) {
    const [name, options] = getSegmentTitleArgs({
      segmentName: selectedSegment,
      productExtended,
    });

    return tCommon(name, options);
  }

  return (
    <ImpressionsBatcherProvider
      batchLocationId="pdpYouMightAlsoLike"
      impressionsType="productImpressions"
      eventType={ActivityTrackerEventType.PRODUCT_IMPRESSIONS}
    >
      <div className={styles.segments}>
        {(Object.entries(segments) as Entries<typeof segments>).map(
          ([segmentName, products]) => {
            if (products.length < MIN_PRODUCTS_PER_SEGMENT) {
              return null;
            }

            const visibleProducts = !isMobile
              ? products.slice(
                  0,
                  YMAL_DESKTOP_PRODUCTS_PER_SEGMENT_VISIBLE_ON_PAGE
                )
              : products;

            return (
              <React.Fragment key={segmentName}>
                <div
                  className={styles.segment}
                  // eslint-disable-next-line no-restricted-syntax
                  data-testid={`segment--${segmentName}`}
                >
                  <SimilarItems
                    title={getSegmentTitle(segmentName)}
                    products={visibleProducts}
                    itemsPerRow={itemsPerRow}
                    minItemWidth={144}
                    onProductClick={(product, index) =>
                      handleProductClick(product, index, segmentName)
                    }
                    moduleName={ModuleName.YouMightAlsoLike}
                    headerButton={
                      getIsSeeMoreButtonDisplayed(products) && (
                        <ButtonMinimal
                          onClick={() => handleSeeMoreClick(segmentName)}
                          className={styles.seeMoreText}
                        >
                          <Body as="span" variant="sm-regular">
                            {tCommon('SeeMore')}
                          </Body>
                        </ButtonMinimal>
                      )
                    }
                  />
                </div>
                {segmentName === 'any' && renderAfterFirstSegment && (
                  <div className={styles.segment}>
                    {renderAfterFirstSegment}
                  </div>
                )}
              </React.Fragment>
            );
          }
        )}

        {selectedSegment && (
          <YouMightAlsoLikeModal
            title={getSegmentTitle(selectedSegment)}
            isOpen={!!selectedSegment}
            setOpen={handleModalOpen}
            products={segments[selectedSegment] || []}
            onProductClick={(product, index) =>
              handleProductClick(product, index, selectedSegment)
            }
          />
        )}
      </div>
    </ImpressionsBatcherProvider>
  );
}
